import { useState } from 'react';

import { getErrorMessage } from '@/hooks/useApi';

import { FeatureToggle, useAdminToggles, useUpdateToggle } from './useToggles';

export function TogglesPage() {
  const { data: toggles, isLoading, error } = useAdminToggles();
  const updateToggle = useUpdateToggle();
  const [editingConfig, setEditingConfig] = useState<string | null>(null);
  const [configValue, setConfigValue] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);

  const handleToggle = (toggle: FeatureToggle) => {
    updateToggle.mutate({
      toggleId: toggle.toggleId,
      enabled: !toggle.enabled,
    });
  };

  const handleEditConfig = (toggle: FeatureToggle) => {
    setEditingConfig(toggle.toggleId);
    setConfigValue(toggle.config ? JSON.stringify(toggle.config, null, 2) : '{}');
    setConfigError(null);
  };

  const handleSaveConfig = (toggleId: string) => {
    try {
      const parsedConfig = JSON.parse(configValue);
      setConfigError(null);
      updateToggle.mutate(
        { toggleId, config: parsedConfig },
        {
          onSuccess: () => {
            setEditingConfig(null);
          },
        }
      );
    } catch (_error) {
      setConfigError('Invalid JSON');
    }
  };

  const handleCancelConfig = () => {
    setEditingConfig(null);
    setConfigValue('');
    setConfigError(null);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Feature Toggles</h1>
        <div className="animate-pulse text-slate-500">Loading toggles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Feature Toggles</h1>
        <div className="text-red-600">Error loading toggles: {getErrorMessage(error)}</div>
      </div>
    );
  }

  // Group toggles by type
  const groupedToggles = {
    ops: toggles?.filter((t) => t.type === 'ops') || [],
    config: toggles?.filter((t) => t.type === 'config') || [],
    release: toggles?.filter((t) => t.type === 'release') || [],
  };

  const typeLabels = {
    ops: { label: 'Operations', description: 'Kill switches and operational controls' },
    config: { label: 'Configuration', description: 'Feature configuration settings' },
    release: { label: 'Feature Releases', description: 'Gradual feature rollouts' },
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Feature Toggles</h1>
        <p className="text-slate-600 mt-1">
          Manage environment-wide feature flags and configuration
        </p>
      </div>

      {updateToggle.error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          Failed to update toggle: {getErrorMessage(updateToggle.error)}
        </div>
      )}

      <div className="space-y-8">
        {(['ops', 'config', 'release'] as const).map((type) => (
          <div key={type}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-800">{typeLabels[type].label}</h2>
              <p className="text-sm text-slate-500">{typeLabels[type].description}</p>
            </div>

            {groupedToggles[type].length === 0 ? (
              <p className="text-slate-400 text-sm italic">No {type} toggles found</p>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
                {groupedToggles[type].map((toggle) => (
                  <div key={toggle.toggleId} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <code className="text-sm font-mono bg-slate-100 px-2 py-0.5 rounded">
                            {toggle.toggleId}
                          </code>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                              toggle.enabled
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {toggle.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{toggle.description}</p>
                        {toggle.config && Object.keys(toggle.config).length > 0 && (
                          <div className="mt-2">
                            {editingConfig === toggle.toggleId ? (
                              <div className="space-y-2">
                                <textarea
                                  value={configValue}
                                  onChange={(e) => setConfigValue(e.target.value)}
                                  className={`w-full px-3 py-2 text-sm font-mono border rounded-lg focus:outline-none focus:ring-2 ${
                                    configError
                                      ? 'border-red-300 focus:ring-red-500'
                                      : 'border-slate-300 focus:ring-indigo-500'
                                  }`}
                                  rows={4}
                                />
                                {configError && (
                                  <p className="text-xs text-red-600">{configError}</p>
                                )}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleSaveConfig(toggle.toggleId)}
                                    disabled={updateToggle.isPending}
                                    className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                                  >
                                    {updateToggle.isPending ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={handleCancelConfig}
                                    className="px-3 py-1.5 text-xs bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <code className="text-xs font-mono bg-slate-50 px-2 py-1 rounded text-slate-600">
                                  {JSON.stringify(toggle.config)}
                                </code>
                                <button
                                  onClick={() => handleEditConfig(toggle)}
                                  className="text-xs text-indigo-600 hover:text-indigo-800"
                                >
                                  Edit
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          Updated {new Date(toggle.updatedAt).toLocaleString()} by{' '}
                          {toggle.updatedBy}
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggle(toggle)}
                        disabled={updateToggle.isPending}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 ${
                          toggle.enabled ? 'bg-indigo-600' : 'bg-slate-200'
                        }`}
                        role="switch"
                        aria-checked={toggle.enabled}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            toggle.enabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {!toggles || toggles.length === 0 ? (
        <div className="mt-8 p-8 bg-slate-50 rounded-lg text-center">
          <p className="text-slate-500">No toggles found.</p>
          <p className="text-sm text-slate-400 mt-1">
            Run the seed script to populate initial toggles.
          </p>
        </div>
      ) : null}
    </div>
  );
}
